/**
 * LoadHorizon — Act II of the Two-Act Stage (founder-approved
 * round7-dashlayout-1-twoact): when the power arrives.
 *
 * READS REAL DATA ONLY:
 *   - Stacked energized-MW staircase from REAL phase MW increments
 *     (`phaseIncrementMW` SSOT) at REAL phase CODs.
 *   - Phases with bucket-only or missing CODs render as HATCHED provisional
 *     ribbons riding the firm stack ("~2-5 years" / "UNDATED") — positioned
 *     for context, never asserted, and never beneath a firm block.
 *   - TODAY cursor at the current date; FULL BUILD cap = Σ ultimate campus MW.
 *   - Sites with no chartable phase data are footnoted (orbit-only), never
 *     invented.
 *
 * A11y: the chart is role="img" with a data summary; the milestone rail and
 * footnote carry the same facts as text. Cross-light with the orbit rides the
 * shared activeSiteId state. All animation (left-to-right wipe, milestone
 * pops, today pulse) is CSS-only, gated to the final frame under
 * prefers-reduced-motion (media query + data-motion attribute twin).
 */
import React from 'react';
import { useSectionViewedTelemetry } from './useSectionViewedTelemetry';
import {
  addContourBlock,
  contourValueAt,
  useHorizonData,
  usePrefersReducedMotion,
  type ContourPoint,
  type HorizonEvent,
} from './twoActDerivation';
import './twoAct.css';

/* ── Geometry (viewBox units; mirrors the approved mock) ──────────────────── */

const VB_W = 1500;
const VB_H = 600;
const XL = 90;
const XR = 1455;
const YB = 540;
const YT = 80;

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

function fmtMW(mw: number): string {
  return Math.round(mw).toLocaleString('en-US');
}

/** Nice axis step targeting ~5 gridlines: 1/2/2.5/5 × 10^k. */
function niceStep(maxValue: number): number {
  const raw = maxValue / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.max(1, raw))));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (raw <= m * mag) return m * mag;
  }
  return 10 * mag;
}

interface BlockShape {
  event: HorizonEvent;
  /** SVG path (already y-mapped). */
  d: string;
  /** Top-of-stack MW right after this block's ramp — milestone drop anchor. */
  topMW: number;
  x: number;
}

interface ChartModel {
  blocks: BlockShape[];
  maxMW: number;
  yOf: (mw: number) => number;
  xOf: (ms: number) => number;
  years: number[];
  gridMWs: number[];
  unit: 'MW' | 'GW';
  unitOf: (mw: number) => string;
}

function buildChartModel(
  firm: HorizonEvent[],
  provisional: HorizonEvent[],
  fullBuildMW: number,
  now: Date,
): ChartModel {
  const startMs = new Date(now.getFullYear(), 0, 1).getTime();
  const lastEventMs = Math.max(
    now.getTime(),
    ...firm.map((e) => e.dateMs),
    ...provisional.map((e) => e.dateMs),
  );
  const endYear = Math.max(
    now.getFullYear() + 6,
    new Date(lastEventMs).getFullYear() + 1,
  );
  const endMs = new Date(endYear, 0, 1).getTime();
  const xOf = (ms: number): number =>
    XL + ((Math.min(Math.max(ms, startMs), endMs) - startMs) / (endMs - startMs)) * (XR - XL);

  // Stack: firm first (their staircase is independent of any estimate), then
  // provisional ribbons RIDE the full firm contour — exactly like the mock.
  const rampW = Math.max(8, xOf(startMs + MS_PER_YEAR / 2) - XL);
  let contour: ContourPoint[] = [
    { x: XL, v: 0 },
    { x: XR, v: 0 },
  ];
  const shapes: Array<{ event: HorizonEvent; polygon: ContourPoint[]; topMW: number; x: number }> = [];
  for (const event of [...firm, ...provisional]) {
    const x = xOf(event.dateMs);
    const { polygon, contour: next } = addContourBlock(contour, x, event.mw, rampW);
    shapes.push({
      event,
      polygon,
      topMW: contourValueAt(next, Math.min(x + rampW, XR)),
      x,
    });
    contour = next;
  }

  const stackTop = contourValueAt(contour, XR);
  const maxMW = Math.max(stackTop, fullBuildMW, 1) * 1.08;
  const yOf = (mw: number): number => YB - (mw / maxMW) * (YB - YT);

  const blocks: BlockShape[] = shapes.map((s) => ({
    event: s.event,
    topMW: s.topMW,
    x: s.x,
    d: `M${s.polygon.map((p) => `${p.x.toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' L')} Z`,
  }));

  const yearStride = endYear - now.getFullYear() > 8 ? 2 : 1;
  const years: number[] = [];
  for (let y = now.getFullYear(); y <= endYear; y += yearStride) years.push(y);

  const step = niceStep(maxMW);
  const gridMWs: number[] = [];
  for (let v = step; v <= maxMW; v += step) gridMWs.push(v);

  const unit: 'MW' | 'GW' = maxMW >= 1500 ? 'GW' : 'MW';
  const unitOf = (mw: number): string =>
    unit === 'GW' ? (mw / 1000).toFixed(1) : `${Math.round(mw)}`;

  return { blocks, maxMW, yOf, xOf, years, gridMWs, unit, unitOf };
}

/* ── Component ─────────────────────────────────────────────────────────────── */

interface LoadHorizonProps {
  customerId: string | null;
  /** Cross-light state shared with the Power-Mix Orbit. */
  activeSiteId?: string | null;
  onActiveSiteChange?: (siteId: string | null) => void;
  /** Left-rail site filter — must match the orbit's so both acts agree. */
  visibleSiteIds?: ReadonlySet<string> | null;
  className?: string;
}

const MILESTONE_CAP = 12;

export function LoadHorizon({
  customerId,
  activeSiteId = null,
  onActiveSiteChange,
  visibleSiteIds = null,
  className = '',
}: LoadHorizonProps) {
  const sectionRef = useSectionViewedTelemetry('load-horizon');
  const data = useHorizonData(customerId, visibleSiteIds);
  const reducedMotion = usePrefersReducedMotion();
  const uid = React.useId();
  const now = React.useMemo(() => new Date(), []);

  const model = React.useMemo(
    () => buildChartModel(data.firmEvents, data.provisionalEvents, data.fullBuildMW, now),
    [data.firmEvents, data.provisionalEvents, data.fullBuildMW, now],
  );

  const hasEvents = model.blocks.length > 0;
  const crossLight = (siteId: string): string =>
    activeSiteId == null ? '' : activeSiteId === siteId ? ' is-lit' : ' is-dim';
  const dimOnly = (siteId: string): string =>
    activeSiteId == null || activeSiteId === siteId ? '' : ' is-dim';

  // Group blocks per site so cross-light addresses whole sites.
  const blocksBySite = React.useMemo(() => {
    const map = new Map<string, { siteName: string; blocks: BlockShape[] }>();
    for (const block of model.blocks) {
      const entry = map.get(block.event.siteId) ?? { siteName: block.event.siteName, blocks: [] };
      entry.blocks.push(block);
      map.set(block.event.siteId, entry);
    }
    return map;
  }, [model.blocks]);

  // Milestone rail: chronological, two stagger lanes, capped to stay legible.
  const milestones = React.useMemo(
    () => [...model.blocks].sort((a, b) => a.x - b.x).slice(0, MILESTONE_CAP),
    [model.blocks],
  );
  const hiddenMilestones = model.blocks.length - milestones.length;

  const summary = hasEvents
    ? `Load horizon: ${fmtMW(data.chartedMW)} MW charted across ${data.firmEvents.length} dated phase${data.firmEvents.length === 1 ? '' : 's'}${
        data.provisionalEvents.length > 0
          ? ` and ${data.provisionalEvents.length} provisional phase${data.provisionalEvents.length === 1 ? '' : 's'} (hatched, not asserted)`
          : ''
      }; full build ${fmtMW(data.fullBuildMW)} MW.`
    : 'Load horizon: no dated phases yet.';

  const todayX = model.xOf(now.getTime());
  const todayLabel = now
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    .toUpperCase();

  return (
    <section
      ref={sectionRef}
      className={`hz-panel ${className}`.trim()}
      aria-label="Load horizon"
      data-motion={reducedMotion ? 'reduced' : 'full'}
      data-testid="load-horizon"
    >
      <div className="hz-head">
        <span className="da-type-eyebrow">Load Horizon</span>
        <span className="hz-key">
          <span><i className="hz-sw hz-sw--btm" />BTM (BRIDGE)</span>
          <span><i className="hz-sw hz-sw--grid" />GRID IMPORT</span>
          <span><i className="hz-sw hz-sw--nuclear" />NUCLEAR OPTION</span>
          <span><i className="hz-sw hz-sw--pend" />PROVISIONAL · NOT ASSERTED</span>
        </span>
      </div>

      {hasEvents ? (
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={summary}>
          <defs>
            <pattern
              id={`${uid}-hatch`}
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" style={{ fill: 'color-mix(in srgb, var(--text-muted) 10%, transparent)' }} />
              <line x1="0" y1="0" x2="0" y2="6" style={{ stroke: 'color-mix(in srgb, var(--text-muted) 50%, transparent)' }} strokeWidth="1.2" />
            </pattern>
          </defs>

          {/* axis frame */}
          <g className="hz-ax">
            {model.gridMWs.map((v) => (
              <React.Fragment key={v}>
                <line className="hz-gl" x1={XL} y1={model.yOf(v)} x2={XR} y2={model.yOf(v)} />
                <text className="hz-ylab" x={XL - 8} y={model.yOf(v) + 2.5}>
                  {model.unitOf(v)}
                </text>
              </React.Fragment>
            ))}
            <text className="hz-yunit" x={XL - 8} y={YT - 8}>{model.unit}</text>
            <line className="hz-bl" x1={XL} y1={YB} x2={XR} y2={YB} />
            {model.years.map((year) => {
              const x = model.xOf(new Date(year, 0, 1).getTime());
              return (
                <React.Fragment key={year}>
                  <line className="hz-yt" x1={x} y1={YB} x2={x} y2={YB + 6} />
                  <text className="hz-xlab" x={x} y={YB + 20}>{year}</text>
                </React.Fragment>
              );
            })}
          </g>

          {/* the energization staircase — wiped in left-to-right */}
          <g className="hz-chart">
            {[...blocksBySite.entries()].map(([siteId, group]) => (
              <g
                key={siteId}
                className={`hz-group${crossLight(siteId)}`}
                data-hz={siteId}
                data-testid={`horizon-site-${siteId}`}
                onMouseEnter={() => onActiveSiteChange?.(siteId)}
                onMouseLeave={() => onActiveSiteChange?.(null)}
              >
                <title>{group.siteName}</title>
                {group.blocks.map((block) => (
                  <path
                    key={`${siteId}-${block.event.phaseId}${block.event.provisional ? '-provisional' : ''}`}
                    className={`hz-blk ${block.event.provisional ? 'hz-blk--pend' : `hz-blk--${block.event.category}`}`}
                    d={block.d}
                    fill={block.event.provisional ? `url(#${uid}-hatch)` : undefined}
                    data-testid={`horizon-block-${siteId}-${block.event.phaseLabel}${block.event.provisional ? '-provisional' : ''}`}
                  />
                ))}
              </g>
            ))}

            {/* today cursor */}
            <g className="hz-today" data-testid="horizon-today">
              <text className="hz-today-text" x={todayX} y={16}>TODAY · {todayLabel}</text>
              <line className="hz-today-line" x1={todayX} y1={22} x2={todayX} y2={YB} />
            </g>

            {/* full-build cap — Σ ultimate campus MW */}
            {data.fullBuildMW > 0 ? (
              <>
                <line
                  className="hz-capline"
                  x1={XL}
                  y1={model.yOf(data.fullBuildMW)}
                  x2={XR}
                  y2={model.yOf(data.fullBuildMW)}
                />
                <text className="hz-cap" x={XR - 5} y={model.yOf(data.fullBuildMW) - 6} data-testid="horizon-full-build">
                  FULL BUILD · {fmtMW(data.fullBuildMW)} MW
                </text>
              </>
            ) : null}
          </g>

          {/* milestone rail : smart stagger lanes, ticks pop with the wipe front */}
          {(() => {
            const labelPositions: { x: number; y: number }[] = [];
            return milestones.map((block, i) => {
              let lane = 0;
              let y1 = 16;
              let hasOverlap = true;
              while (hasOverlap && lane < 8) {
                hasOverlap = labelPositions.some(
                  (pos) => Math.abs(pos.x - block.x) < 260 && Math.abs(pos.y - y1) < 30
                );
                if (hasOverlap) {
                  lane++;
                  y1 = 16 + lane * 42;
                }
              }
              labelPositions.push({ x: block.x, y: y1 });

              const isFirst = !block.event.provisional && block.event === data.firmEvents[0];
              const cls = [
                'hz-ms',
                isFirst ? 'hz-ms--first' : '',
                block.event.provisional ? 'hz-ms--pend' : '',
                dimOnly(block.event.siteId),
              ].filter(Boolean).join(' ');
              return (
              <g
                key={`${block.event.siteId}-${block.event.phaseId}${block.event.provisional ? '-provisional' : ''}`}
                className={cls}
                data-hz={block.event.siteId}
                style={{ '--mt': `${(2.32 + i * 0.11).toFixed(2)}s` } as React.CSSProperties}
              >
                <text className="hz-ms1" x={block.x} y={y1}>
                  {(block.event.siteName ?? '').toUpperCase()} {block.event.phaseLabel}
                </text>
                <text className="hz-ms2" x={block.x} y={y1 + 11}>
                  {fmtMW(block.event.mw)} MW · {block.event.dateLabel.toUpperCase()}
                </text>
                <line
                  className="hz-msd"
                  x1={block.x}
                  y1={y1 + 15}
                  x2={block.x}
                  y2={model.yOf(block.topMW) - 3}
                />
                <circle className="hz-msdot" cx={block.x} cy={model.yOf(block.topMW)} r={2} />
              </g>
            );
          });
          })()}
          {hiddenMilestones > 0 ? (
            <text className="hz-ms2" x={XR - 5} y={30} textAnchor="end">
              +{hiddenMilestones} further phase{hiddenMilestones === 1 ? '' : 's'}
            </text>
          ) : null}
        </svg>
      ) : (
        <div className="da-panel__body" data-testid="load-horizon-empty">
          <p className="type-body text-text-secondary">No phase CODs to project yet.</p>
          <p className="type-meta mt-1">
            Add phases with MW and target CODs in each site&apos;s Project Intent to build the
            energization staircase.
          </p>
        </div>
      )}

      {/* orbit-only footnote — sites that cannot be charted, stated plainly */}
      {data.notes.length > 0 ? (
        <div className="hz-foot" data-testid="load-horizon-footnote">
          {data.notes.map((note) => (
            <span key={note.siteId}>
              {note.siteName} — {note.reason === 'no-phases' ? 'no phase data' : 'phases unsized (MW TBD)'} · orbit only
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}
